/**
 * Onboarding tab: the hire's own checklist (plan docs/plans/lms-plan.md 12.3,
 * 12.4, section 13 tab 5, 18.24; WP-47; R1, R14).
 *
 * Replaces WP-03's placeholder in place: LmsApp routes /lms/onboarding to this
 * file's DEFAULT export (a component with no props). GET /me/onboarding
 * answers the caller's active run in this network with its items and
 * progress, or null, which is the empty state "No onboarding checklist is
 * active for you" (13 tab 5).
 *
 * The items are grouped by due week (12.3), each with its description, link,
 * responsible party and evidence. On the hire's own open items: "Mark done"
 * (the evidence the item asks for: a written confirmation, or a PDF, PNG or
 * JPEG up to 10 MB) and "Not applicable" with a reason; a course item offers
 * its course instead (finishing the course completes the item, and only an
 * admin may mark it not applicable, 18.24 LMS_COURSE_ITEM). An admin's item
 * is read-only here ("Waiting on an admin"). Every act answers the item and
 * the run's status; the page updates in place, and when the last item closes
 * the run it says the checklist is complete (12.4).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle2, Check, CircleSlash, ListChecks } from 'lucide-react';
import PageHeader, { RefreshButton } from '../../../ui/PageHeader';
import EmptyState from '../../../ui/EmptyState';
import { useToast } from '../../../ui/Toast';
import { primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { lmsPageCls } from '../../components/layout/page';
import { lmsTab } from '../config';
import useLoad from '../home/useLoad';
import { getMyEvidenceUrl, getMyOnboardingRun, markMyItemDone, markMyItemNotApplicable } from '../../api/onboarding';
import type { MyOnboardingRunView, OnboardingDoneInput, OnboardingItemResult, OnboardingRunItem } from '../../api/onboarding';
import { ItemCard } from './ItemCard';
import { DoneDialog } from './DoneDialog';
import { ReasonDialog } from './ReasonDialog';
import { useEvidenceViewer } from './useEvidenceViewer';
import { applyItemResult, formatDay, groupByDueWeek, localToday, ProgressBlock, RunStatusPill } from './shared';

const TAB = lmsTab('onboarding');

const cardCls = 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800';

export function OnboardingTab() {
  const { toast } = useToast();
  const load = useLoad<MyOnboardingRunView | null>(getMyOnboardingRun, 'Your onboarding checklist could not be loaded.');
  const [doneTarget, setDoneTarget] = useState<OnboardingRunItem | null>(null);
  const [naTarget, setNaTarget] = useState<OnboardingRunItem | null>(null);
  const evidence = useEvidenceViewer();
  const today = localToday();
  const view = load.data;
  const active = view?.run.status === 'active';

  const apply = (result: OnboardingItemResult) => {
    load.setData((prev) => (prev ? applyItemResult(prev, result, today) : prev));
  };

  const markDone = async (input: OnboardingDoneInput) => {
    if (!doneTarget) return;
    const result = await markMyItemDone(doneTarget.id, input);
    apply(result);
    setDoneTarget(null);
    const parts = [`Marked "${result.title}" done.`];
    if (result.external_cert) parts.push('The certificate went to an admin for review.');
    if (result.run_status === 'completed') parts.push('Your onboarding checklist is complete.');
    toast(parts.join(' '), { type: 'success' });
  };

  const markNotApplicable = async (reason: string) => {
    if (!naTarget) return;
    const result = await markMyItemNotApplicable(naTarget.id, reason);
    apply(result);
    setNaTarget(null);
    toast(
      result.run_status === 'completed'
        ? `Marked "${result.title}" not applicable. Your onboarding checklist is complete.`
        : `Marked "${result.title}" not applicable.`,
      { type: 'success' }
    );
  };

  const actionsFor = (item: OnboardingRunItem) => {
    if (!active || item.status !== 'pending' || item.responsible !== 'new_hire') return null;
    if (item.kind === 'course') {
      return item.course_id ? (
        <Link to={`/lms/courses/${encodeURIComponent(item.course_id)}`} className={primaryBtnCls} data-testid={`onboarding-course-${item.id}`}>
          <BookOpen className="h-4 w-4" aria-hidden="true" />
          Start the course
        </Link>
      ) : null;
    }
    return (
      <>
        <button type="button" className={primaryBtnCls} onClick={() => setDoneTarget(item)} data-testid={`onboarding-done-${item.id}`}>
          <Check className="h-4 w-4" aria-hidden="true" />
          Mark done
        </button>
        <button type="button" className={secondaryBtnCls} onClick={() => setNaTarget(item)} data-testid={`onboarding-na-${item.id}`}>
          <CircleSlash className="h-4 w-4" aria-hidden="true" />
          Not applicable
        </button>
      </>
    );
  };

  let body;
  if (load.loading && !view) {
    body = (
      <div className={`${cardCls} space-y-3`} aria-busy="true" data-testid="onboarding-loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-1.5">
            <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-1/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        ))}
      </div>
    );
  } else if (load.error) {
    body = (
      <div
        className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
        aria-live="polite"
        data-testid="onboarding-error"
      >
        <span>{load.error}</span>
        <button type="button" className={secondaryBtnCls} onClick={load.reload}>
          Retry
        </button>
      </div>
    );
  } else if (!view) {
    body = (
      <EmptyState
        icon={ListChecks}
        variant="card"
        title="No onboarding checklist is active for you"
        hint="When an admin starts your onboarding, the things to do outside the platform appear here, with who does each one and when it is due."
      />
    );
  } else {
    const groups = groupByDueWeek(view.items, view.run.start_date);
    const pendingMine = view.items.filter((i) => i.status === 'pending' && i.responsible === 'new_hire').length;
    body = (
      <>
        <section className={cardCls} aria-labelledby="lms-onboarding-progress-title" data-testid="onboarding-summary">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="lms-onboarding-progress-title" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Your checklist
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-600 dark:text-gray-400">{`Started ${formatDay(view.run.start_date)}`}</span>
              <RunStatusPill status={view.run.status} testId="onboarding-run-status" />
            </div>
          </div>
          <div className="mt-3">
            <ProgressBlock progress={view.progress} testId="onboarding-progress" />
          </div>
          {view.run.status === 'completed' ? (
            <p
              className="mt-3 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"
              role="status"
              data-testid="onboarding-complete"
            >
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Your onboarding checklist is complete.
            </p>
          ) : (
            <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
              {pendingMine === 0
                ? "Nothing is waiting on you. The open items are an admin's to finish."
                : `${pendingMine} ${pendingMine === 1 ? 'item is' : 'items are'} waiting on you.`}
            </p>
          )}
        </section>

        {groups.map((g) => (
          <section key={g.week} aria-labelledby={`lms-onboarding-week-${g.week}`} data-testid={`onboarding-week-${g.week}`}>
            <div className="mb-2 flex flex-wrap items-baseline gap-2">
              <h2 id={`lms-onboarding-week-${g.week}`} className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {`Week ${g.week}`}
              </h2>
              <span className="text-xs text-gray-600 dark:text-gray-400">{`Due ${formatDay(g.from)} to ${formatDay(g.to)}`}</span>
            </div>
            <ul className="space-y-2">
              {g.items.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  today={today}
                  viewer="hire"
                  actions={actionsFor(item)}
                  onViewEvidence={() => evidence.view(item.id, () => getMyEvidenceUrl(item.id, 'inline'))}
                  viewing={evidence.opening === item.id}
                  blockedUrl={evidence.blocked?.id === item.id ? evidence.blocked.url : null}
                />
              ))}
            </ul>
          </section>
        ))}
      </>
    );
  }

  return (
    <div className={lmsPageCls} data-testid="lms-onboarding">
      <PageHeader
        title={TAB.title}
        icon={TAB.icon}
        subtitle={TAB.description}
        actions={<RefreshButton onClick={load.reload} loading={load.loading} label="Refresh the checklist" />}
      />
      {body}

      <DoneDialog item={doneTarget} onClose={() => setDoneTarget(null)} onSubmit={markDone} />
      <ReasonDialog
        isOpen={naTarget !== null}
        title="Not applicable"
        intro={naTarget ? <p className="font-medium text-gray-900 dark:text-gray-100">{naTarget.title}</p> : null}
        label="Reason"
        hint="Why this item does not apply to you (for example, done at a previous employer)."
        confirmLabel="Mark not applicable"
        onClose={() => setNaTarget(null)}
        onSubmit={markNotApplicable}
        testId="onboarding-na"
      />
    </div>
  );
}

export default OnboardingTab;
